import { allPages } from "@/lib/pagination";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState } from "@/components/PageBits";
import { CodeBlock } from "@/components/CodeBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, KeyRound, RefreshCw, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const ALL_PERMS = ["rooms:read", "rooms:write", "tokens:create", "usage:read", "calls:read", "calls:write", "meetings:read", "meetings:write", "recordings:read", "recordings:write"];

export default function ApiKeys() {
  const { projectId, currentProject } = useWorkspace();
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [perms, setPerms] = useState(["rooms:read", "rooms:write", "tokens:create"]);
  const [reveal, setReveal] = useState(null); // {full_key, ...}

  const load = () => { if (projectId) allPages(`/projects/${projectId}/api-keys`).then((data) => setKeys(data)).catch(() => {}); };
  useEffect(load, [projectId]);

  const togglePerm = (p) => setPerms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const create = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/api-keys`, { name, permissions: perms });
      setReveal(data); setOpen(false); setName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const rotate = async (id) => {
    try { const { data } = await api.post(`/api-keys/${id}/rotate`); setReveal({ ...data, name: "rotated key" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revoke = async (id) => {
    try { await api.delete(`/api-keys/${id}`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createBtn = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-apikey-button" disabled={!projectId} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">
          <Plus className="mr-2 h-4 w-4" /> Create key
        </Button>
      </DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="new-apikey-dialog">
        <DialogHeader><DialogTitle className="text-zinc-100">Create API key</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><Label className="text-zinc-300">Name</Label>
            <Input data-testid="apikey-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-950 border-zinc-800" placeholder="server-backend" /></div>
          <div className="space-y-2"><Label className="text-zinc-300">Permissions</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_PERMS.map((p) => (
                <label key={p} className="flex items-center gap-2 rounded-md border border-zinc-800 px-3 py-2 text-sm text-zinc-300">
                  <Checkbox data-testid={`perm-${p}`} checked={perms.includes(p)} onCheckedChange={() => togglePerm(p)} />
                  <span className="font-mono text-xs">{p}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-apikey-confirm" disabled={!name} onClick={create} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="apikeys-page">
      <PageHeader title="API Keys" description={`Server-side credentials for ${currentProject?.name || "your project"}. Use as X-API-Key header.`} actions={createBtn} />

      {/* reveal-once dialog */}
      <Dialog open={!!reveal} onOpenChange={(v) => !v && setReveal(null)}>
        <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="apikey-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100"><ShieldAlert className="h-5 w-5 text-amber-400" /> Copy your secret now</DialogTitle>
            <DialogDescription className="text-zinc-400">This is the only time the secret will be shown. Store it securely.</DialogDescription>
          </DialogHeader>
          {reveal && <CodeBlock testId="apikey-fullkey" language="X-API-Key" code={reveal.full_key} />}
          <DialogFooter><Button data-testid="apikey-reveal-done" onClick={() => setReveal(null)} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {!projectId ? (
        <EmptyState title="No project selected" description="Select a project first." />
      ) : keys.length === 0 ? (
        <EmptyState testId="apikeys-empty" title="No API keys" description="Create a key to call the LiveCore API from your backend." action={createBtn} />
      ) : (
        <div className="overflow-hidden rounded-md border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Public ID</th><th className="px-4 py-3 font-medium">Permissions</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {keys.map((k) => (
                <tr key={k.id} data-testid={`apikey-row-${k.name}`}>
                  <td className="px-4 py-3 text-zinc-100"><div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-zinc-500" />{k.name}</div></td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{k.public_id}</td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{k.permissions.map((p) => <span key={p} className="rounded border border-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">{p}</span>)}</div></td>
                  <td className="px-4 py-3">{k.revoked ? <span className="text-xs text-red-400">revoked</span> : <span className="text-xs text-emerald-400">active</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`rotate-apikey-${k.name}`} onClick={() => rotate(k.id)} title="Rotate" className="rounded border border-zinc-800 p-1.5 text-zinc-400 hover:bg-zinc-800"><RefreshCw className="h-3.5 w-3.5" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`revoke-apikey-${k.name}`} title="Revoke" className="rounded border border-red-900/50 p-1.5 text-red-400 hover:bg-red-950/40"><Trash2 className="h-3.5 w-3.5" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="border-zinc-800 bg-zinc-950">
                          <AlertDialogHeader><AlertDialogTitle className="text-zinc-100">Revoke “{k.name}”?</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">Any backend using this key will immediately lose access.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel className="border-zinc-800 bg-zinc-900">Cancel</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-revoke-${k.name}`} onClick={() => revoke(k.id)} className="bg-red-600 text-white hover:bg-red-500">Revoke</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
